/**
 * AdminDatabaseBrowserSection - admin can view/CRUD every collection in the system.
 * Aligned with phygital_full_seeded_database_master.md
 * Bidirectional save: changes here propagate to runtime via shared MongoDB.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, Database, Edit3, Save, Trash2, Plus, RefreshCw, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Group collections by domain - mirrors the master doc structure
const GROUPS = [
  { label: "Identity & Access", names: ["users","organizations","org_members","host_access_assignments","marketing_personas","advocates","permission_bundles","role_visibility_rules"] },
  { label: "Categories & Catalog", names: ["categories","category_templates","occasions","launch_control"] },
  { label: "Portals & Flows", names: ["pilot_portals","portal_access_registry","pilot_intake_templates","pilot_intake_submissions","demo_sales_pages","demo_access_links"] },
  { label: "Provider System", names: ["provider_profiles","provider_services","provider_clients","provider_appointments","provider_addresses","provider_product_submissions","my_providers"] },
  { label: "Events & RSVPs", names: ["events","event_rsvps","event_passes","event_waitlist","waitlist"] },
  { label: "Planning Surfaces", names: ["user_defaults","user_preferences","user_planned_experiences","experiences"] },
  { label: "Products & Looks", names: ["looks","look_products","master_products","brands"] },
  { label: "Inspiration & Cards", names: ["user_inspo","inspo_folders","inspo_items","preference_cards","preference_card_sections","preference_field_definitions","share_links","share_events"] },
  { label: "Media", names: ["media_library","ui_assets","final_look_media_links"] },
  { label: "Notifications & Messaging", names: ["notifications","notification_templates","notification_preferences","messages"] },
  { label: "Beehiiv (Bidirectional Sync)", names: ["beehiiv_custom_fields","beehiiv_tags","beehiiv_sync_rules","beehiiv_automation_drafts","beehiiv_engagement_sync"] },
  { label: "Subscriptions & Financials", names: ["pricing_tiers","subscription_templates","subscriptions","subscription_assignments","live_projections","projection_assumptions","commission_tracking","host_payment_settings","financial_projections","revenue_shares"] },
  { label: "Copy & UI", names: ["copy_registry","screen_registry","modal_registry","drawer_registry","userflows","userflow_steps","feature_to_screen_map","screen_data_map"] },
  { label: "Auth & NDAs", names: ["magic_links","dashboard_share_links","portal_invites","nda_templates","nda_signatures"] },
  { label: "Sub-categories & Feedback", names: ["portal_sub_categories","product_feedback","product_features"] },
  { label: "QA & Ops", names: ["bugs","tests","qa_evidence","activity_log","audit_logs","admin_audit","analytics_events","referral_events"] },
  { label: "Misc", names: ["lash_mappings","lash_mapping_presets","service_resources","service_product_usage","service_inspo_usage","event_questions"] },
];

export default function AdminDatabaseBrowserSection() {
  const [collections, setCollections] = useState([]);
  const [selected, setSelected] = useState(null);
  const [records, setRecords] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editJson, setEditJson] = useState("");
  const [creating, setCreating] = useState(false);
  const [createJson, setCreateJson] = useState("{}");

  const headers = useCallback(() => {
    const t = localStorage.getItem("session_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const loadCollections = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/database/collections`, { headers: headers(), withCredentials: true });
      setCollections(res.data?.collections || []);
    } catch (e) { toast.error("Failed to load collections"); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { loadCollections(); }, [loadCollections]);

  const loadRecords = async (name) => {
    setSelected(name);
    setRecordsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/database/${name}/records?limit=200`, { headers: headers(), withCredentials: true });
      setRecords(res.data?.records || []);
    } catch (e) { toast.error("Failed to load records"); }
    finally { setRecordsLoading(false); }
  };

  const findId = (rec) => {
    if (!rec) return null;
    return Object.entries(rec).find(([k]) => k.endsWith("_id") && k !== "_id")?.[1] || rec._id;
  };

  const saveEdit = async () => {
    if (!editing || !selected) return;
    try {
      const data = JSON.parse(editJson);
      const id = findId(editing);
      await axios.put(`${API}/admin/database/${selected}/record/${id}`, data, { headers: headers(), withCredentials: true });
      toast.success("Saved. Synced to runtime");
      setEditing(null);
      loadRecords(selected);
    } catch (e) {
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const create = async () => {
    if (!selected) return;
    try {
      const data = JSON.parse(createJson);
      await axios.post(`${API}/admin/database/${selected}/records`, data, { headers: headers(), withCredentials: true });
      toast.success("Created");
      setCreating(false);
      setCreateJson("{}");
      loadRecords(selected);
    } catch (e) {
      toast.error("Create failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const remove = async (rec) => {
    if (!selected) return;
    if (!confirm(`Delete this ${selected} record? This cannot be undone.`)) return;
    try {
      const id = findId(rec);
      await axios.delete(`${API}/admin/database/${selected}/record/${id}`, { headers: headers(), withCredentials: true });
      toast.success("Deleted");
      loadRecords(selected);
    } catch (e) {
      toast.error("Delete failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const filteredRecords = records.filter(r => {
    if (!search) return true;
    return JSON.stringify(r).toLowerCase().includes(search.toLowerCase());
  });

  // Build collection lookup
  const colMap = Object.fromEntries(collections.map(c => [c.name, c]));

  return (
    <div className="space-y-4" data-testid="admin-database-browser">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2"><Database className="w-5 h-5" /> Database Browser</h1>
          <p className="text-sm text-gray-500">All {collections.length} collections. Full CRUD, bidirectional sync to runtime.</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadCollections} data-testid="db-refresh-btn">
          <RefreshCw className="w-3 h-3 mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Left: collection list grouped */}
        <div className="col-span-4 space-y-3">
          <ScrollArea className="h-[calc(100vh-260px)]">
            {loading ? <p className="text-sm text-gray-400">Loading…</p> : (
              <div className="space-y-3 pr-2">
                {GROUPS.map(g => {
                  const items = g.names.map(n => colMap[n]).filter(Boolean);
                  if (items.length === 0) return null;
                  return (
                    <div key={g.label}>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">{g.label}</p>
                      <div className="space-y-0.5">
                        {items.map(c => (
                          <button
                            key={c.name}
                            onClick={() => loadRecords(c.name)}
                            className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs hover:bg-gray-100 ${selected === c.name ? "bg-black text-white hover:bg-gray-800" : ""}`}
                            data-testid={`db-coll-${c.name}`}
                          >
                            <span className="truncate text-left">{c.name}</span>
                            <Badge variant={selected === c.name ? "secondary" : "outline"} className="text-[9px] ml-2 shrink-0">{c.count}</Badge>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {/* Any collections not in groups */}
                {(() => {
                  const grouped = new Set(GROUPS.flatMap(g => g.names));
                  const orphans = collections.filter(c => !grouped.has(c.name));
                  if (orphans.length === 0) return null;
                  return (
                    <div>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Other</p>
                      <div className="space-y-0.5">
                        {orphans.map(c => (
                          <button
                            key={c.name}
                            onClick={() => loadRecords(c.name)}
                            className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-xs hover:bg-gray-100 ${selected === c.name ? "bg-black text-white hover:bg-gray-800" : ""}`}
                            data-testid={`db-coll-${c.name}`}
                          >
                            <span className="truncate text-left">{c.name}</span>
                            <Badge variant={selected === c.name ? "secondary" : "outline"} className="text-[9px] ml-2 shrink-0">{c.count}</Badge>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Right: records */}
        <div className="col-span-8">
          {!selected ? (
            <Card><CardContent className="p-8 text-center text-sm text-gray-400">Select a collection from the left to view records.</CardContent></Card>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <h2 className="font-semibold text-sm">{selected}</h2>
                <Badge variant="outline" className="text-[10px]">{filteredRecords.length} of {records.length}</Badge>
                <div className="ml-auto flex items-center gap-2">
                  <div className="relative w-56">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                    <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="pl-7 h-8 text-xs" data-testid="db-records-search" />
                  </div>
                  <Button size="sm" onClick={() => setCreating(true)} data-testid="db-create-btn"><Plus className="w-3 h-3 mr-1" /> New</Button>
                </div>
              </div>

              <ScrollArea className="h-[calc(100vh-310px)]">
                <div className="space-y-2 pr-2">
                  {recordsLoading ? <p className="text-sm text-gray-400">Loading…</p> :
                    filteredRecords.length === 0 ? <p className="text-sm text-gray-400">No records.</p> :
                    filteredRecords.map((rec, i) => {
                      const idVal = findId(rec);
                      const titleField = rec.display_name || rec.name || rec.title || rec.user_id || rec.email_primary || rec.email || idVal;
                      return (
                        <Card key={idVal || i} className="hover:bg-gray-50">
                          <CardContent className="p-3 flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{titleField}</p>
                              <p className="text-[10px] text-gray-400 truncate">{idVal}</p>
                              <pre className="text-[9px] text-gray-500 mt-1 line-clamp-2 whitespace-pre-wrap break-all">{JSON.stringify(rec, null, 0).slice(0, 200)}…</pre>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setEditing(rec); setEditJson(JSON.stringify(rec, null, 2)); }} data-testid={`db-edit-${idVal}`}>
                                <Edit3 className="w-3 h-3" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => remove(rec)} data-testid={`db-delete-${idVal}`}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })
                  }
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader><DialogTitle>Edit record in <code className="text-sm">{selected}</code></DialogTitle></DialogHeader>
          <div className="flex-1 overflow-auto">
            <Textarea value={editJson} onChange={(e) => setEditJson(e.target.value)} className="font-mono text-xs min-h-[400px]" data-testid="db-edit-textarea" />
            <p className="text-[10px] text-gray-500 mt-1">Edit raw JSON. _id is auto-stripped on save. updated_at is auto-set.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="db-save-btn"><Save className="w-3 h-3 mr-1" /> Save & Sync</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader><DialogTitle>New record in <code className="text-sm">{selected}</code></DialogTitle></DialogHeader>
          <div className="flex-1 overflow-auto">
            <Textarea value={createJson} onChange={(e) => setCreateJson(e.target.value)} className="font-mono text-xs min-h-[400px]" placeholder='{"key": "value"}' data-testid="db-create-textarea" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={create} data-testid="db-create-confirm"><Plus className="w-3 h-3 mr-1" /> Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
